import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { Location } from '@angular/common';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { CustomerService } from '../../services/customer.service';
import { GlobalSearchService } from '../../services/globalsearchservice.service';
import { ReportsService } from '../../services/reports.service';

export interface RepBulkPreviewCounts {
	total: number;
	missing_invoice_email: number;
	ediinvoices_off: number;
	emailstatement_off: number;
	fully_configured: number;
	invalid_noemail: number;
	pending_b2b: number;
}

export interface RepBulkSampleRow {
	debtorno: string;
	name: string;
	ediinvoices: boolean;
	emailstatement: boolean;
	invoice_email: string;
	email_status: string;
}

@Component({
	selector: 'app-rep-bulk-enable',
	templateUrl: './rep-bulk-enable.component.html',
	styleUrls: ['./rep-bulk-enable.component.scss'],
})
export class RepBulkEnableComponent implements OnInit, OnDestroy {
	color = 'blue';
	config: any;
	salespeople: any[] = [];
	isSalesman = false;
	sessionSalesmanCode = '';

	salesmanCtrl = new UntypedFormControl('');
	actionEdiInvoices = true;
	actionEmailStatement = true;
	actionSyncContacts = true;

	loadingPreview = false;
	applying = false;
	previewError = '';
	applyError = '';
	applyResult: any = null;

	salesmancode = '';
	counts: RepBulkPreviewCounts | null = null;
	samples: RepBulkSampleRow[] = [];

	private userSub?: Subscription;
	private spSub?: Subscription;
	private ctrlSub?: Subscription;

	readonly countCards: { key: keyof RepBulkPreviewCounts; label: string }[] = [
		{ key: 'total', label: 'Total customers' },
		{ key: 'missing_invoice_email', label: 'Missing invoice email' },
		{ key: 'ediinvoices_off', label: 'Auto invoice off' },
		{ key: 'emailstatement_off', label: 'Email statement off' },
		{ key: 'fully_configured', label: 'Fully configured' },
		{ key: 'invalid_noemail', label: 'Invalid / noemail' },
		{ key: 'pending_b2b', label: 'Pending B2B access' },
	];

	constructor(
		public globalSearchService: GlobalSearchService,
		private customerService: CustomerService,
		private reportsService: ReportsService,
		private location: Location,
		private router: Router,
		private cdr: ChangeDetectorRef
	) {
		this.globalSearchService.configsubscription.subscribe((r) => {
			this.config = r;
			this.color = this.globalSearchService.getColor();
		});
	}

	ngOnInit(): void {
		this.color = this.globalSearchService.getColor();
		this.globalSearchService.mesagesallowed.next(false);

		this.spSub = this.reportsService.getSalesPeople().subscribe((rows: any) => {
			this.salespeople = rows || [];
			this.applySalesmanDefaults();
			this.cdr.markForCheck();
		});

		this.userSub = this.globalSearchService.user.subscribe(() => {
			this.applySalesmanDefaults();
			this.cdr.markForCheck();
		});

		this.ctrlSub = this.salesmanCtrl.valueChanges.subscribe(() => {
			this.previewError = '';
			this.applyResult = null;
		});
	}

	ngOnDestroy(): void {
		this.userSub?.unsubscribe();
		this.spSub?.unsubscribe();
		this.ctrlSub?.unsubscribe();
	}

	back(): void {
		this.location.back();
	}

	get canRunActions(): boolean {
		return !this.loadingPreview && !this.applying && this.selectedActions.length > 0 && !!this.resolveSalesmancode();
	}

	get selectedActions(): string[] {
		const actions: string[] = [];
		if (this.actionEdiInvoices) {
			actions.push('ediinvoices');
		}
		if (this.actionEmailStatement) {
			actions.push('emailstatement');
		}
		if (this.actionSyncContacts) {
			actions.push('sync_contacts');
		}
		return actions;
	}

	get salesmanLabel(): string {
		const code = this.resolveSalesmancode();
		if (!code) {
			return '';
		}
		const sp = this.salespeople.find((s) => String(s?.salesmancode ?? '').trim() === code);
		return sp ? `${code} — ${sp.salesmanname}` : code;
	}

	private applySalesmanDefaults(): void {
		const auth = this.globalSearchService.user.getValue();
		const u = auth?.user;
		this.isSalesman = !!u?.issalesman;
		this.sessionSalesmanCode = String(u?.salesman ?? '').trim();

		if (this.isSalesman && this.sessionSalesmanCode) {
			this.salesmanCtrl.setValue(this.sessionSalesmanCode, { emitEvent: false });
			if (!this.counts && !this.loadingPreview) {
				this.loadPreview();
			}
		}
	}

	resolveSalesmancode(): string {
		if (this.isSalesman && this.sessionSalesmanCode) {
			return this.sessionSalesmanCode;
		}
		return String(this.salesmanCtrl.value ?? '').trim();
	}

	loadPreview(): void {
		const code = this.resolveSalesmancode();
		if (!code) {
			this.previewError = 'Select a salesperson to load the preview.';
			return;
		}

		this.loadingPreview = true;
		this.previewError = '';
		this.applyError = '';
		this.applyResult = null;

		const previewArg = this.isSalesman ? undefined : code;
		this.customerService.repBulkPreview(previewArg).subscribe({
			next: (res: any) => {
				this.loadingPreview = false;
				if (!res?.success) {
					this.previewError = res?.message || 'Preview failed.';
					this.counts = null;
					this.samples = [];
					this.cdr.markForCheck();
					return;
				}
				this.salesmancode = res.salesmancode || code;
				this.counts = res.counts || null;
				this.samples = Array.isArray(res.samples) ? res.samples : [];
				this.cdr.markForCheck();
			},
			error: (err: any) => {
				this.loadingPreview = false;
				this.previewError = err?.error?.message || err?.message || 'Preview request failed.';
				this.counts = null;
				this.samples = [];
				this.cdr.markForCheck();
			},
		});
	}

	runDryRun(): void {
		this.runApply(true);
	}

	confirmApply(): void {
		if (!this.canRunActions) {
			return;
		}
		const code = this.resolveSalesmancode();
		const msg =
			`Enable email settings for customers assigned to rep ${code}?\n\n` +
			`Actions: ${this.selectedActions.join(', ')}\n\n` +
			`Customers without a valid email will be skipped.`;
		if (!window.confirm(msg)) {
			return;
		}
		this.runApply(false);
	}

	private runApply(dryRun: boolean): void {
		const code = this.resolveSalesmancode();
		if (!code || !this.selectedActions.length) {
			return;
		}

		this.applying = true;
		this.applyError = '';
		this.applyResult = null;

		const payload: {
			salesmancode?: string;
			debtornos?: string[];
			actions: string[];
			dryRun: boolean;
		} = {
			actions: this.selectedActions,
			dryRun,
		};

		if (!this.isSalesman) {
			payload.salesmancode = code;
		}

		this.customerService.repBulkApply(payload).subscribe({
			next: (res: any) => {
				this.applying = false;
				if (!res?.success) {
					this.applyError = res?.message || 'Apply failed.';
					this.cdr.markForCheck();
					return;
				}
				this.applyResult = res;
				const verb = dryRun ? 'Dry run complete' : 'Bulk update complete';
				const count = dryRun ? `${res.updated} would update` : `${res.updated} updated`;
				this.globalSearchService.showNotification(
					`${verb}: ${count}, ${res.skipped} skipped.`,
					'success',
					'bottom',
					'right'
				);
				if (!dryRun) {
					this.loadPreview();
				}
				this.cdr.markForCheck();
			},
			error: (err: any) => {
				this.applying = false;
				this.applyError = err?.error?.message || err?.message || 'Apply request failed.';
				this.cdr.markForCheck();
			},
		});
	}

	emailStatusLabel(status: string): string {
		switch (status) {
		case 'configured':
			return 'Configured';
		case 'partial':
			return 'Partial';
		case 'missing':
			return 'Missing email';
		case 'invalid_noemail':
			return 'Invalid / noemail';
		default:
			return status || '—';
		}
	}

	openCustomer(debtorno: string): void {
		if (!debtorno) {
			return;
		}
		this.router.navigate(['/customers/view', debtorno]);
	}
}
